import { Button } from "@patient-watch/ui/button";
import { useConsentCopy } from "@/features/i18n/consentCopy";
import { cn } from "@/utils/cn";
import { Outlet, createFileRoute } from "@tanstack/react-router";
import { ChevronDown } from "lucide-react";
import { useEffect, useState } from "react";

const consentPrintStyles = `
@media print {
    @page {
        margin: 14mm;
        size: A4;
    }

    html:has(.consent-print-root),
    body:has(.consent-print-root),
    #app:has(.consent-print-root) {
        background: #fff !important;
        background-color: #fff !important;
        -webkit-print-color-adjust: economy !important;
        print-color-adjust: economy !important;
    }

    body:has(.consent-print-root) [class*="bg-sky"],
    body:has(.consent-print-root) .consent-preview-shell {
        background: #fff !important;
        background-color: #fff !important;
        padding: 0 !important;
        gap: 0 !important;
    }

    body:has(.consent-print-root) header,
    body:has(.consent-print-root) footer {
        display: none !important;
    }

    .consent-print-root,
    .consent-print-root * {
        background: transparent !important;
        background-image: none !important;
        box-shadow: none !important;
        text-shadow: none !important;
        color: #0f172a !important;
    }

    .consent-print-root {
        background: #fff !important;
        min-height: 0 !important;
    }

    .consent-print-root .text-muted-foreground {
        color: #475569 !important;
    }

    .consent-print-root a {
        color: #0f172a !important;
        text-decoration: underline;
    }
}
`;

export const Route = createFileRoute("/preview/consent")({
    component: ConsentPreviewLayout,
});

function ConsentPreviewLayout() {
    const copy = useConsentCopy();
    const [showScrollButton, setShowScrollButton] = useState(true);

    useEffect(() => {
        const checkScrollPosition = () => {
            const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
            const isPastHalfway = scrollTop + clientHeight >= (scrollHeight * 7) / 8;
            setShowScrollButton(!isPastHalfway);
        };

        window.addEventListener("scroll", checkScrollPosition);
        checkScrollPosition();

        return () => window.removeEventListener("scroll", checkScrollPosition);
    }, []);

    const scrollToSignature = () => {
        const signatureElement = document.getElementById("consent-signature");
        if (signatureElement) {
            signatureElement.scrollIntoView({ behavior: "smooth" });
            return;
        }

        window.scrollTo({
            top: document.documentElement.scrollHeight,
            behavior: "smooth",
        });
    };

    return (
        <div className={cn("mx-auto w-full max-w-3xl consent-print-root")}>
            <style>{consentPrintStyles}</style>
            <Outlet />
            {showScrollButton ? (
                <div className="print:hidden bottom-8 left-1/2 fixed px-4 max-w-md -translate-x-1/2">
                    <Button
                        variant="secondary"
                        className={cn(
                            "shadow-md rounded-lg",
                            "transition-all duration-300 hover:translate-y-1",
                            "flex items-center justify-center gap-2",
                            "px-4 py-2"
                        )}
                        onClick={scrollToSignature}
                        aria-label={copy.scrollToSignatureAria}
                    >
                        <span>{copy.goToSignature}</span>
                        <ChevronDown className="w-5 h-5" />
                    </Button>
                </div>
            ) : null}
        </div>
    );
}
